import { httpClient } from "./http-client";
import type { ApiResponse } from "../../shared/types/api";

export type NotificationType =
  | "NEW_ORDER"
  | "ORDER_STATUS_CHANGED"
  | "TODO_ASSIGNED"
  | "TODO_STATUS_CHANGED"
  | "TODO_DEPENDENCY_WARNING"
  | "TODO_DEADLINE_APPROACHING"
  | "TODO_DEADLINE_OVERDUE"
  | "TODO_NOTE_ADDED";

export interface NotificationItem {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  /** 알림 종류별 부가 정보(JSON 문자열). NEW_ORDER 는 { companyId, orderId, orderNumber } */
  data: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string | null;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  totalCount: number;
  unreadCount: number;
}

const NOTIFICATION_ENDPOINTS = {
  LIST: "/api/notifications/list",
  UNREAD_COUNT: "/api/notifications/unread-count",
  READ: (id: number) => `/api/notifications/${id}/read`,
  READ_ALL: "/api/notifications/read-all",
} as const;

class NotificationApiService {
  async getNotifications(page: number, size: number): Promise<NotificationListResponse> {
    const response = await httpClient.get<ApiResponse<NotificationListResponse>>(
      NOTIFICATION_ENDPOINTS.LIST,
      { params: { page, size } },
    );
    return response.data.data ?? { notifications: [], totalCount: 0, unreadCount: 0 };
  }

  async getUnreadCount(): Promise<number> {
    const response = await httpClient.get<ApiResponse<{ unreadCount: number }>>(
      NOTIFICATION_ENDPOINTS.UNREAD_COUNT,
    );
    return response.data.data?.unreadCount ?? 0;
  }

  async markAsRead(id: number): Promise<void> {
    await httpClient.patch(NOTIFICATION_ENDPOINTS.READ(id));
  }

  async markAllAsRead(): Promise<void> {
    await httpClient.patch(NOTIFICATION_ENDPOINTS.READ_ALL);
  }
}

export const notificationApiService = new NotificationApiService();

/** NEW_ORDER 알림의 data 에서 주문 ID 를 꺼낸다. 형식이 다르면 null */
export const parseNotificationOrderId = (item: NotificationItem): number | null => {
  if (!item.data) return null;
  try {
    const parsed = JSON.parse(item.data);
    return typeof parsed?.orderId === "number" ? parsed.orderId : null;
  } catch {
    return null;
  }
};
